import useAxiosRequest from "../hooks/useAxiosRequest";
import {useEffect, useState} from "react";

import '../styles/SpendingTimeline.css';

import config from '../config.json';
import {useSearchParams} from "react-router-dom";

import { Axis, Grid, LineSeries, XYChart } from "@visx/xychart";
import { LegendOrdinal } from "@visx/legend";
import { scaleOrdinal } from "@visx/scale";

const accessors = {
    xAccessor: (d) => new Date(d.timestamp).getTime(),
    yAccessor: (d) => d.spent
};

const SpendingTimeline = () => {
    const [ searchParams ] = useSearchParams();

    const { data, isLoading } = useAxiosRequest({
        url: `${config.backendUrl}/charts/data?id=${searchParams.get("data")}`,
        withCredentials: true,
        method: "get"
    });

    const [ timelineData, setTimelineData ] = useState(null);

    const [ colorScale, setColorScale ] = useState(null);

    useEffect(() => {
        if (!data) return;

        data.events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        const maximumTimestamp = Date.now();
        const minimumTimestamp = Math.min(...data.events.map(event => new Date(event.timestamp).getTime()));

        const partyEvents = data.events.reduce((groups, event) => {
            groups[event.party] = groups[event.party] || [];

            groups[event.party].push(event);

            return groups;
        }, {});

        Object.values(partyEvents).forEach(events => {
            const localMinimumTimestamp = Math.min(...events.map(event => new Date(event.timestamp).getTime()));

            const total = events.reduce((total, event) => {
                total += event.spent;

                event.spent = total;

                return total;
            }, 0);

            events.push({ timestamp: maximumTimestamp, spent: total });
            events.unshift({ timestamp: localMinimumTimestamp, spent: 0 });
            // events.unshift({ timestamp: minimumTimestamp, spent: 0 });
        });

        setTimelineData(partyEvents);

        const scale = scaleOrdinal({
            domain: data.parties.filter(party => partyEvents[party.code]).map(party => party.code),
            range: data.parties.filter(party => partyEvents[party.code]).map(party => party.color)
        });

        setColorScale([scale]);

    }, [data]);

    if (!timelineData || !colorScale) return;

    return (
        <div id={"chart"} style={{ width: "800px", height: "400px", position: "absolute" }}>
            <div style={{ position: "relative", top: "15px", width: "800px", display: "flex", justifyContent: "center", fontWeight: "500", fontSize: "16px" }}>
                <LegendOrdinal
                    scale={colorScale[0]}
                    labelFormat={(code) => data.parties.find(party => party.code === code).shortName || data.parties.find(party => party.code === code).name}
                    direction="row"
                    labelMargin="0 15px 0 0"
                />
            </div>
            <svg width={800} height={400}>
                <rect
                    x={0}
                    y={0}
                    width={800}
                    height={400}
                    fill="white"
                />
                <XYChart
                    height={400}
                    width={800}
                    margin={{ left: 80, top: 30, bottom: 50, right: 27 }}
                    xScale={{ type: "time" }}
                    yScale={{ type: "linear" }}
                >
                    <Grid
                        columns={false}
                        numTicks={5}
                        lineStyle={{
                            stroke: "black",
                            strokeLinecap: "round",
                            strokeWidth: 1
                        }}
                        strokeDasharray="0, 4"
                    />
                    <Axis
                        hideAxisLine
                        hideTicks
                        orientation="bottom"
                        tickLabelProps={() => ({ fill: "black" })}
                        numTicks={8}
                    />
                    <Axis
                        hideAxisLine
                        hideTicks
                        orientation="left"
                        tickLabelProps={() => ({ fill: "black" })}
                        tickFormat={(v) => "$" + v.toLocaleString()}
                    />
                    {Object.entries(timelineData).sort((a, b) => {
                        const partyA = data.parties.find(party => party.code === a[0]);
                        const partyB = data.parties.find(party => party.code === b[0]);

                        return (partyB.order || 0) - (partyA.order || 0);
                    })
                        .map(([ code, data ]) => <LineSeries key={code} stroke={colorScale[0](code)} strokeWidth={3} dataKey={code} data={data} {...accessors} />)}
                </XYChart>
            </svg>
        </div>
    );
}

export default SpendingTimeline;
